import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { di } from 'react-magnetic-di';
import { mapDispatchToPropsBanner } from 'state/persisted/blocked-apps-banner/reducer';
import ErrorBoundary from 'view/error-boundary/error-boundary';
import { ForgeCustomFieldEdit } from 'view/forge-ui/custom-field';
import { getExtensionIdFromLegacyFieldType, isForgeLegacyFieldType } from 'view/forge-ui/custom-field/utils';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, xcss } from '@atlaskit/primitives';
import { FieldBase } from '@atlassian/help-center-common-component/fields/field-base';
import type { FieldFormProps } from '@atlassian/help-center-common-component/fields/types';
import {
    CUSTOM_FIELD,
    CUSTOM_FIELD_TYPE,
    FORGE_REQUEST_CREATE,
    FORGE_REQUEST_DETAILS,
} from '@atlassian/help-center-common-component/forge-ui/constants';
import { AtlassianHelpConfigAccountType } from '@atlassian/help-center-common-component/forge-ui/types';
import type {
    ExtensionPointModule,
    CustomField,
    CustomFieldType,
    CustomFieldExtensionData,
    CustomFieldExtensionPayload,
    WithBlockedAppsBannerProps,
} from '@atlassian/help-center-common-component/forge-ui/types';
import { useShowBanner } from '@atlassian/help-center-common-util/blocked-apps-banner';
import { getMetaFromHTML } from '@atlassian/help-center-common-util/meta';
import { useExtensions } from '../../forge-ui/use-extensions';
import { Skeleton } from './skeleton';
import { getMappedFieldType } from './utils/utils';
import type { FieldType } from 'state/persisted/request-create/types';

export interface Props extends FieldFormProps<string>, WithBlockedAppsBannerProps {
    entryPoint: 'view' | 'edit';
    value?: string;
    jiraFieldType: string;
    disabled: boolean; // TODO: https://ecosystem-platform.atlassian.net/browse/ARKEN-2153
    portalId: number;
    requestTypeId: number;
    onChange: (name: string, value: unknown, notFromUserInteraction?: boolean) => void;
    enableSubmitButton: () => void;
    disableSubmitButton: () => void;
    fallbackRender: (newType: FieldType) => JSX.Element;
}

const ForgeFieldWithExtension: React.FC<
    Props & {
        extension: CustomField | CustomFieldType;
        loadingComponent: React.ReactNode;
        isVisible: boolean;
    }
> = ({ id, value, extension, requestTypeId, portalId, entryPoint, loadingComponent, isVisible, onChange }) => {
    di(ForgeCustomFieldEdit);

    const extensionData: CustomFieldExtensionData = useMemo(
        () => ({
            fieldType: extension.id,
            fieldId: id,
            type: extension.type,
            portal: {
                id: portalId,
            },
            request: {
                typeId: requestTypeId,
            },
            ...(entryPoint === 'view'
                ? {
                      renderContext: 'portal-view',
                      experience: 'portal-view',
                      entryPoint: 'view',
                  }
                : {
                      renderContext: 'portal-request',
                      experience: 'portal-request',
                      entryPoint: 'edit',
                  }),
        }),
        [extension.id, extension.type, id, portalId, requestTypeId, entryPoint]
    );

    const extensionPayload: CustomFieldExtensionPayload = useMemo(() => ({ fieldValue: value }), [value]);
    const onSubmit = useCallback(
        (fieldValue: unknown) => onChange(extensionData.fieldId, fieldValue, true),
        [extensionData.fieldId, onChange]
    );

    if (entryPoint === 'edit') {
        return (
            <Box xcss={isVisible ? visibleStyles : hiddenStyles}>
                <ForgeCustomFieldEdit
                    extension={extension}
                    extensionData={extensionData}
                    extensionPayload={extensionPayload}
                    loadingComponent={loadingComponent}
                    onSubmit={onSubmit}
                />
            </Box>
        );
    }

    return null;
};

const ForgeFieldImpl = (props: Props) => {
    di(getMetaFromHTML, useExtensions, FieldBase, Skeleton);

    const { disableSubmitButton, enableSubmitButton, fallbackRender, jiraFieldType, entryPoint } = props;
    const [isRendered, setIsRendered] = useState(false);
    const accountType = getMetaFromHTML('ajs-account-type') || AtlassianHelpConfigAccountType.ANONYMOUS;
    const hasAccess = accountType === AtlassianHelpConfigAccountType.ATLASSIAN;
    const useExtensionsConfig = useMemo(
        () => ({
            types: [CUSTOM_FIELD, CUSTOM_FIELD_TYPE] as ExtensionPointModule[],
            context: {},
            includeHidden: true,
            shouldFetch: hasAccess,
            includeEgressOverridesForge: fg('forge-ui-overrides-property-in-extension-list'),
        }),
        [hasAccess]
    );
    const { extensions, isLoading, error } = useExtensions<CustomField | CustomFieldType>(useExtensionsConfig);
    const onRendered = useCallback(() => setIsRendered(true), []);

    useEffect(() => {
        isLoading ? disableSubmitButton() : enableSubmitButton();
    }, [isLoading, disableSubmitButton, enableSubmitButton]);

    const extension = useMemo(() => {
        const extensionId = isForgeLegacyFieldType(jiraFieldType)
            ? getExtensionIdFromLegacyFieldType(jiraFieldType)
            : jiraFieldType;
        return extensions.find((field) => field.id === extensionId);
    }, [extensions, jiraFieldType]);

    // We are going to pass to the list only the extension that is found.
    // This is to avoid showing the banner for the extensions that are not added to the experience but still installed on the tenant.
    const extensionsToCheck = useMemo(() => (extension ? [extension] : []), [extension]);
    useShowBanner(
        extensionsToCheck,
        entryPoint === 'view' ? FORGE_REQUEST_DETAILS : FORGE_REQUEST_CREATE,
        hasAccess,
        props.showBanner,
        props.resetBannerState
    );

    const shouldFallBackToNativeRendering = useMemo(() => {
        // Too early to tell, just assume we have a custom renderer
        if (!extension) return false;
        // There is no resource, so it's not a custom renderer
        if (typeof extension.properties[entryPoint]?.resource !== 'string') return true;
        // There must be a correct experience defined, otherwise we'd need to fallback to the native rendering
        const isValidExperience =
            entryPoint === 'view'
                ? extension.properties[entryPoint]?.experience?.includes('portal-view')
                : extension.properties[entryPoint]?.experience?.includes('portal-request');
        return !isValidExperience;
    }, [extension, entryPoint]);

    const preferredFieldNode = useMemo(() => {
        if (shouldFallBackToNativeRendering && extension) {
            return fallbackRender(getMappedFieldType(extension));
        }
        return null;
    }, [extension, fallbackRender, shouldFallBackToNativeRendering]);

    const ContainerComponent = useMemo(() => {
        const ForgeFieldContainer = ({ children }: { children: React.ReactNode }) => (
            <FieldBase
                htmlFor={props.id}
                label={props.label}
                required={props.required}
                description={props.description}
                errorMessage={props.errorMessage}
                labelId={`${props.id}-label`}
            >
                {children}
            </FieldBase>
        );
        return ForgeFieldContainer;
    }, [props.description, props.errorMessage, props.id, props.label, props.required]);

    const isHidden = typeof extension?.hiddenBy === 'string';

    if (!hasAccess || isHidden) {
        return null;
    }

    if (error) {
        // TODO: https://ecosystem-platform.atlassian.net/browse/ARKEN-2156
        return (
            <ContainerComponent>
                <div>Error</div>
            </ContainerComponent>
        );
    }

    if (!extension && !isLoading) {
        // TODO: https://ecosystem-platform.atlassian.net/browse/ARKEN-2157
        // Check whether there should be no error state, only error message should be reported (reproduce it on issue create and do the same as done in there)
        return (
            <ContainerComponent>
                <div>Not found</div>
            </ContainerComponent>
        );
    }

    if (preferredFieldNode) {
        return preferredFieldNode;
    }

    return (
        <ContainerComponent>
            {isLoading || !isRendered ? <Skeleton /> : null}
            {extension && (
                <ForgeFieldWithExtension
                    {...props}
                    extension={extension}
                    loadingComponent={<Skeleton onUnmount={onRendered} />}
                    isVisible={isRendered}
                />
            )}
        </ContainerComponent>
    );
};

export const ForgeFieldWithErrorBoundary = (props: Props) => (
    <ErrorBoundary
        id={props.entryPoint === 'view' ? `forge-custom-field-view` : 'forge-custom-field-edit'}
        packageName="portal-fields"
    >
        <ForgeFieldImpl {...props} />
    </ErrorBoundary>
);

export const ForgeField = connect(null, mapDispatchToPropsBanner)(ForgeFieldWithErrorBoundary);

const visibleStyles = xcss({});
const hiddenStyles = xcss({ display: 'none' });
